/* import __COLOCATED_TEMPLATE__ from './project-modal.hbs'; */
import { scheduleOnce } from '@ember/runloop';
import { computed, getProperties } from '@ember/object';
import { notEmpty, or, readOnly, sort } from '@ember/object/computed';
import { A } from '@ember/array';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { handleErrorResponseWithModal, handleErrorResponse } from 'sh-www/utils/handles-errors';
import { closeModal } from 'sh-www/utils/modal-controls';
import { IMAGE_COMMANDS__MODEL_TYPES } from '../../../services/commands/image-commands';
import {
  VIDEO_CATEGORIES,
  VIDEO_CATEGORY_MODELS
} from '../../../constants/video-categories';
import { isUrl } from '../../../utils/misc';
import { workSampleUrlValidator } from 'sh-www/utils/validators';
import Validator from 'validator';
import { stopClickEvent } from '../../../utils/events';
import { validateSchema } from 'sh-www/utils/validators';
import { task, timeout } from 'ember-concurrency';
import { array, string, object, boolean } from 'yup';

const CLASS_NAME = 'SH-c-work-sample-project';

const SCHEMA = object({
  '_title': string().required('You must provide a project title.'),
  'projectWasDoneOnPlatform': boolean(),
  '_link': string()
    .when('projectWasDoneOnPlatform', {
      is: (val) => !val,
      then: (schema) => schema.required('You must provide a link.'),
      otherwise: (schema) => schema.optional()
    })
    .test(
      'link-requirement',
      'Link must be a valid URL',
      function(value) {
        const protocols = ['http', 'https'];
        return value ? Validator.isURL(value, { protocols }) : true;
      }
    ),
  '_description': string().required('You must provide a project description.'),
  '_serviceIDs': array().required('Please select at least one service')
    .min(1, 'Please select at least one service')
});

const FORM_INPUTS = [
  '_title',
  '_link',
  '_description',
  '_serviceIDs',
  '_videoCategoryID',
  '_videoTopicIDs',
];

const THUMBNAIL_FETCH_DEBOUNCE_MS = 300;

const THUMBNAIL_LOAD_ERROR = "We were unable to load a video thumbnail. This may indicate that the video is not visible to the public. If possible, review the video's privacy setting.";

/**
 * @param {Object} videoCategory
 * @returns {{ key: String, model: Object }}
 */
const mapVideoCategoryToOption = (videoCategory) => ({
  key: videoCategory.label,
  model: videoCategory,
});

/** The component for editing a work sample project. */
export default Component.extend({
  tagName: '',

  classNames: [CLASS_NAME],
  CLASS_NAME,

  applicationStateManager: service('state-managers/application'),
  imageCommandService : service('commands/image-commands'),
  oembedCommandService : service('commands/oembed-commands'),
  projectdigestCommandService: service('commands/projectdigest-commands'),
  errorTracker: service('error-tracker'),
  session: service(),
  shData: service(),
  store: service(),

  currentUser: readOnly('session.currentUser'),

  /** @param {ProjectDigest} projectDigest */
  projectDigest: null,

  /** @param {Boolean} isAddLinkToPublishedProject */
  isAddLinkToPublishedProject: false,

  /** @param {Boolean} isAddingMissingProjectInfo */
  isAddingMissingProjectInfo: false,

  /** @param {Function} onModalClose */
  onModalClose: () => {},

  /** @param {Function} showDeleteModal */
  onShowDeleteModal: () => {},

  /** @param {Function} addProjectDigestCallback */
  addProjectDigestCallback: () => {},

  /** @param {Function} removeProjectDigestCallback */
  removeProjectDigestCallback: () => {},

  _title: '',
  _link: '',
  _description: '',
  _serviceIDs: A([]),
  _videoCategoryID: '',
  _videoTopicIDs: A([]),

  _fetchedThumbnail: '',

  projectWasDoneOnPlatform: readOnly('projectDigest.wasDoneOnPlatform'),

  /** @type {Boolean} Whether the ProjectDigest exists. */
  _projectDigestExists: notEmpty('projectDigest.id'),

  /** @type {Boolean} Whether the ProjectDigest has been edited by the user. */
  _projectDigestHasBeenEdited: notEmpty('projectDigest.editedAt'),

  linkErrorMessage: computed(
    'projectDigest.{badLink,link}',
    '_link',
    'errors._link',
    function() {
      if (this.errors._link) {
        return this.errors._link;
      }

      if (this.projectDigest?.badLink && this._link === this.projectDigest?.link) {
        return 'This link cannot be reached';
      }
    },
  ),

  /** @type {Boolean} Whether to show the thumbnail associated. */
  _showThumbnail: computed(
    '_thumbnailImageSrc',
    '_link',
    '_isThumbnailPreviewLoading',
    '_thumbnailError',
    function () {
      return this._isThumbnailPreviewLoading
        || this._thumbnailError
        || (this._thumbnailImageSrc && this._link);
    },
  ),

  /** @type {String} The modal title. */
  _titleText: computed(
    '_projectDigestHasBeenEdited',
    'projectWasDoneOnPlatform',
    function () {
      if (this.projectWasDoneOnPlatform) return 'Edit project';

      return this.get('_projectDigestHasBeenEdited') ? 'Edit project' : 'Add project';
    },
  ),

  /** @type {Boolean} Whether a request is currently underway. */
  _isSubmitting: false,

  /** @type {Boolean} Whether the user is required to double opt in to edit the link. */
  _doubleOptIn: false,

  /** @type {Ember.A<Service>} The models of the selected services. */
  _serviceModels: computed('_serviceIDs.[]', function () {
    return this.get('_serviceIDs').map((serviceID) => {
      return this.get('store').peekRecord('service', serviceID);
    });
  }),

  /** @type {string} The string value for the selected video category. */
  _videoCategorySelectedOption: computed('_videoCategoryID', function () {
    const id = this.get('_videoCategoryID');
    return id ? VIDEO_CATEGORIES[id] : '';
  }),

  /** @type {Ember.A<Service>} The models of the service options. */
  _serviceOptions: A([]),

  /** @type {Array<String>} */
  serviceSortKeys: ['category', 'name'],

  /** @type {Ember.A<Service>} */
  sortedServices: sort('_serviceOptions', 'serviceSortKeys'),

  /** @type {Ember.A<Object>|null} The (pseudo) models of the video category options. */
  _videoCategoryOptions: null,

  /** @type {String} The '_link' value that is saved when the user does a thumbnail upload. */
  _uploadedThumbnailAssociatedLink: '',

  /** @type {Boolean} Whether a thumbnail is currently being uploaded. */
  _isUploadingThumbnail: false,

  /** @type {Image} A thumbnail uploaded by the user. */
  _uploadedThumbnail: null,

  /** @type {String} The URL for the uploaded thumbnail. */
  _uploadedThumbnailURL: computed('_uploadedThumbnail.{cloudVersion,cloudId}', function () {
    const thumbnail = this.get('_uploadedThumbnail');

    if (!thumbnail) return '';

    const { cloudVersion, cloudId } = thumbnail.getProperties('cloudVersion', 'cloudId');

    return this.get('imageCommandService').cloudinaryThumbnailURL(cloudVersion, cloudId);
  }),

  /** @type {String} The file type of the uploaded thumbnail. */
  _uploadedThumbnailType: '',

  /** @type {String} The error message for any thumbnail related error. */
  _thumbnailError: '',

  /** @type {Boolean} Whether the user is allowed to edit the link. */
  _canEditLink: computed(
    'projectDigest.projectdigestVerifications.[]',
    'projectDigest.{confirm,rejected,badLink}',
    'projectWasDoneOnPlatform',
    '_doubleOptIn',
    function () {
      //Creating a new project
      if (!this.projectDigest) {
        return true;
      }
      //If the link is bad and preventing verification, allow the link to be changed
      if (this.projectDigest.badLink) {
        return true;
      }
      // Allow for on-platform projects.
      if (this.projectWasDoneOnPlatform) {
        return true
      };
      // Allow for projects that have had their verification rejected.
      if (this.projectDigest.rejected) {
        return true
      };
      // Allow for projects that have been verified, as long as the user confirms.
      if (this.projectDigest.confirm) {
        return this._doubleOptIn
      };
      // Allow for projects that have not requested verification.
      if (this.projectDigest.projectdigestVerifications.length === 0) {
        return true
      };

      return false;
    },
  ),

  /** @type {Boolean} Whether the user is allowed to edit the services. */
  _canEditServices: computed(
    'projectDigest.projectdigestVerifications.[]',
    'projectDigest.{confirm,rejected}',
    'projectWasDoneOnPlatform',
    '_doubleOptIn',
    function () {
      //Allow for new projects
      if (!this.projectDigest) return true;

      // Disallow for on-platform projects.
      if (this.projectWasDoneOnPlatform) return false;

      // Allow for projects that have had their verification rejected.
      if (this.projectDigest.rejected) return true;

      // Allow for projects that have been verified, as long as the user confirms.
      if (this.projectDigest.confirm) return this._doubleOptIn;

      // Allow for projects that have not requested verification.
      if (this.projectDigest.projectdigestVerifications.length === 0) return true;

      return false;
    },
  ),

  /** @type {String} The error message to show for the services field */
  _servicesErrorMessage: computed(
    '_canEditServices',
    'errors._serviceIDs',
    function () {
      // If disabled, show nothing so that the 'Need to change this?' text can appear there instead.
      if (!this.get('_canEditServices')) return '';

      return this.errors._serviceIDs ?? '';
    },
  ),

  /** @type {Boolean} Whether to show the services field. */
  _showServices: computed(
    'isAddLinkToPublishedProject',
    'projectWasDoneOnPlatform',
    function () {
      if (this.get('isAddLinkToPublishedProject')) return false;

      return !this.projectWasDoneOnPlatform;
    },
  ),

  _showDeleteProject: computed(
    '_projectDigestExists',
    'projectDigest.{application,link}',
    function () {
      return this._projectDigestExists && (!this.projectDigest.application || this.projectDigest.link);
    },
  ),

  /** @type {Boolean} Whether thumbnail preview is loading. */
  _isThumbnailPreviewLoading: or('_isUploadingThumbnail', '_updateThumbnailPreview.isRunning'),

  /** @type {Boolean} Whether this project is verified. */
  _isVerified: readOnly('projectDigest.confirm'),

  /** @type {String|null} The thumbnail to show. */
  _thumbnailImageSrc: computed(
    '_link',
    '_fetchedThumbnail',
    'projectDigest.{link,thumbnailUrl}',
    '_uploadedThumbnailURL',
    '_uploadedThumbnailAssociatedLink',
    function () {
      const existingLink = this.get('projectDigest.link');
      const existingImage = this.projectDigest?.thumbnailUrl;

      const newLink = this.get('_link');
      const newImage = this.get('_uploadedThumbnailURL');

      const newImageLink = this.get('_uploadedThumbnailAssociatedLink');

      if (newLink === newImageLink && newImage) {
        return newImage;
      }

      if (newLink === existingLink && existingImage) {
        return existingImage;
      }

      return this._fetchedThumbnail;
    },
  ),

  init() {
    this._super(...arguments);
    this.set('errors', { });

    this.loadProjectInfoFromModel();

    this.loadServiceOptions();
    this.loadVideoCategoryOptions();
  },

  loadProjectInfoFromModel() {
    const project = this.get('projectDigest');

    if (!project) {
      this.setProperties({
        _title: '',
        _link: '',
        _description: '',
        _serviceIDs: A([]),
        _videoCategoryID: '',
        _videoTopicIDs: A([]),
      });

      return;
    }

    this.setProperties({
      _title: project.get('title') || '',
      _link: project.get('link') || '',
      _description: project.get('description') || '',
      _serviceIDs: A(project.get('services')).map((model) => model.get('id')),
      _videoCategoryID: project.get('videoCategory.id') || '',
      _videoTopicIDs: A(project.get('videoTopics')).map((model) => model.get('id')),
    });
  },

  loadServiceOptions() {
    this.set('_serviceOptions', A([]));

    this.store
      .findAll('service', {
        include: 'service-synonyms',
        adapterOptions: { isPublic: true },
      })
      .then((services) => {
        this.set('_serviceOptions', services.sortBy('category', 'name'));
      })
      .catch((error) => {
        this.errorTracker.error('loadServiceOptions - failed to load services', this, error);
      });
  },

  loadVideoCategoryOptions() {
    const videoCategoryModels = A(VIDEO_CATEGORY_MODELS);
    const videoCategoryOptions = videoCategoryModels.map(mapVideoCategoryToOption);

    this.set('_videoCategoryOptions', videoCategoryOptions);
  },

  getProjectData() {
    return {
      title: this.get('_title'),
      link: this.get('_link'),
      description: this.get('_description'),
      relationships: {
        services: {
          data: this.get('_serviceIDs').map((id) => ({ id, type: 'services' })),
        },
      },
      video_category_id: this.get('_videoCategoryID'),
      video_topic_ids: this.get('_videoTopicIDs'),
    };
  },

  createProject() {
    const user = this.get('currentUser');
    const data = this.getProjectData();
    const thumbnail = this._uploadedThumbnail;

    this.set('_isSubmitting', true);

    return this.get('projectdigestCommandService').createProjectDigest(user, data)
      .then((projectDigest) => this.saveCustomThumbnail.perform(projectDigest, thumbnail))
      .then((projectDigest) => this.reloadImage(projectDigest))
      .then((projectDigest) => {
        this.set('_isSubmitting', false);
        this.addProjectDigestCallback(projectDigest);
        this.onShowVerificationMethodModal({ projectDigest });
      })
      .catch((error) => {
        this.set('_isSubmitting', false);
        handleErrorResponse(this, error, FORM_INPUTS);
      });
  },

  updateProject() {
    const user = this.get('currentUser');
    const projectDigest = this.get('projectDigest');
    const data = this.getProjectData();
    const thumbnail = this._uploadedThumbnail;

    this.set('_isSubmitting', true);

    return this.get('projectdigestCommandService').updateProjectDigest(user, projectDigest, data)
      .then((projectDigest) => this.saveCustomThumbnail.perform(projectDigest, thumbnail))
      .then((projectDigest) => this.reloadImage(projectDigest))
      .then((projectDigest) => {
        this.set('_isSubmitting', false);

        /**
         * The project was already verified, but the user decided to remove the verification status
         * in order to edit certain fields. Since it now needs to be re-verified, show the client
         * info modal.
         */
        if (this.get('_doubleOptIn')) {
          this.onShowVerificationMethodModal({ projectDigest });
          return;
        }

        /**
         * The project digest was missing information that is required for verification. Now that it
         * has been submitted successfully, show the client info modal.
         */
        if (this.get('isAddingMissingProjectInfo')) {
          this.onShowVerificationMethodModal({ projectDigest });
          return;
        }

        this.get('applicationStateManager').addToast('Project updated.');
        this.onModalClose();
      })
      .catch((error) => {
        this.set('_isSubmitting', false);
        handleErrorResponse(this, error, FORM_INPUTS);
      });
  },

  deleteProject() {
    const projectDigest = this.projectDigest;

    return this.projectdigestCommandService.deleteProjectDigest(projectDigest, false)
      .then(() => {
        projectDigest.deleteRecord();
        this.removeProjectDigestCallback?.(projectDigest);
        this.applicationStateManager.addToast('Project deleted');
        closeModal(this);
      }).catch((e) => {
        handleErrorResponseWithModal(this, e, 'Could not delete project digest');
      });
  },

  saveCustomThumbnail: task(function *(projectDigest, thumbnail) {
    if (!thumbnail) return projectDigest;

    const params = [
      IMAGE_COMMANDS__MODEL_TYPES.PROJECT_DIGEST,
      projectDigest.get('id'),
      thumbnail.get('width'),
      thumbnail.get('height'),
      thumbnail.get('cloudId'),
      thumbnail.get('cloudVersion'),
      this.get('_uploadedThumbnailType'),
    ];

    yield this.imageCommandService.saveNewImageForModel(...params);
    return projectDigest;
  }).enqueue(),

  reloadImage(projectDigest) {
    return this.projectdigestCommandService.getProjectDigest(projectDigest.id, ['image'])
      .then((response) => {
        this.shData.pushResponseToStore(response);
        return this.store.peekRecord('projectdigest', response.data.id);
      });
  },

  _updateThumbnailPreview: task(function *(url) {
    if (!isUrl(url)) return;
    if (workSampleUrlValidator(url)) return;

    yield timeout(THUMBNAIL_FETCH_DEBOUNCE_MS);

    try {
      const thumbnailUrl = yield this.oembedCommandService.getThumbnail(url, true);
      this.setProperties({
        _fetchedThumbnail: thumbnailUrl,
        _thumbnailError: '',
      });
    } catch (e) {
      this.setProperties({
        _fetchedThumbnail: '',
        _thumbnailError: THUMBNAIL_LOAD_ERROR,
      });
    }
  }).restartable(),

  actions: {
    onTitleInput(value) {
      this.setProperties({
        _title: value,
        'errors._title': undefined,
      });
    },

    onLinkInput(value) {
      this.setProperties({
        _link: value,
        _thumbnailError: '',
        'errors._link': undefined,
      });

      this._updateThumbnailPreview.perform(value);
    },

    onDescriptionInput(value) {
      this.setProperties({
        _description: value,
        'errors._description': undefined,
      });
    },

    onServiceAdded(service) {
      this.get('_serviceIDs').pushObject(service.get('id'));
      this.set('errors._serviceIDs', undefined);
    },

    onServiceRemoved(service) {
      this.get('_serviceIDs').removeObject(service.get('id'));
    },

    onVideoCategoryChange(videoCategory) {
      this.set('_videoCategoryID', videoCategory.id);
    },

    onVideoTopicAdded(videoTopic) {
      this.get('_videoTopicIDs').pushObject(videoTopic.get('id'));
    },

    onVideoTopicRemoved(videoTopic) {
      this.get('_videoTopicIDs').removeObject(videoTopic.get('id'));
    },

    onDoubleOptIn(fieldName) {
      this.set('_doubleOptIn', true);

      scheduleOnce('afterRender', this, () => {
        document.querySelector(`.${CLASS_NAME}__field--${fieldName} input`).focus();
      });
    },

    onThumbnailChange(event) {
      const [file] = event.target.files;
      event.target.value = '';

      this.setProperties({
        '_isUploadingThumbnail': true,
        '_thumbnailError': '',
      });

      this.get('imageCommandService').createDraftImage(file)
        .then((image) => {
          this.setProperties({
            _uploadedThumbnail: image,
            _uploadedThumbnailType: file.type,
            _uploadedThumbnailAssociatedLink: this.get('_link'),
          });
        })
        .catch((error) => {
          this.get('errorTracker').error(error);
          this.set(
            '_thumbnailError',
            'There was an error uploading your thumbnail. Please try again',
          );
        })
        .finally(() => {
          this.set('_isUploadingThumbnail', false);
        });
    },

    onThumbnailLoadError() {
      this.set('_thumbnailError', THUMBNAIL_LOAD_ERROR);
    },

    onChangeThumbnailClick(event) {
      stopClickEvent(event);
      document.querySelector(`.${CLASS_NAME}__file-input`).click();
    },

    onDelete() {
      this.onShowDeleteModal({
        titleText: 'Delete project?',
        bodyText: `Are you sure you want to permanently delete the project <em>${this.get('projectDigest.title')}</em>?`,
        actionButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        confirmAction: () => { this.deleteProject(); },
        isShowCancelButton: true,
      });
    },

    onCancel() {
      this.onModalClose();
    },

    onConfirm() {
      const [_, validationErrors = {}] = validateSchema(getProperties(this, Object.keys(SCHEMA.fields)), SCHEMA);
      this.set('errors', validationErrors);

      if (Object.values(this.errors).every(error => !error)) {
        this.get('_projectDigestExists')
          ? this.updateProject()
          : this.createProject();
      }
    },

    scrollToBottomOfModal() {
      scheduleOnce('afterRender', this, function () {
        const modalBody = document.querySelector('.dialog-body');
        modalBody.scrollTop = modalBody.scrollHeight;
      });
    },
  },
});
